import { JSX } from 'preact';

import { EditorProps, ViewerProps, RichProp } from 'client/courses/components/page-builder';
import { ContainerWrapper } from 'client/courses/components/page-builder/container-wrapper';
import { ImgProp } from 'client/courses/components/page-builder/editable';
import {
  getIconClasses,
  getIconStyle,
} from 'client/courses/components/page-builder/card-style-utils';

import { CardLayoutBlock } from './card-layout-block';
import {
  extractReadonlyProps,
  extractBlockState,
  createGenerateState,
  SectionHeader,
} from './common-patterns';

import { CardStyles, IconStyles, PaddingSize } from 'server/types/public-overrides';
import { TestimonialsBlockState } from 'server/types';

const DEFAULT_TESTIMONIAL_CARD_STYLES: CardStyles = {
  borderRadius: '2xl',
  shadow: 'lg',
  borderWidth: 'thin',
  borderColor: '#f3f4f6',
  backgroundType: 'solid',
  backgroundColor: '#ffffff',
  hoverLift: 'none',
};

const DEFAULT_ICON_STYLES: IconStyles = {
  shape: 'circle',
  size: 'sm',
  shadow: 'none',
  borderWidth: 'none',
  borderColor: '#e5e7eb',
  backgroundColor: '#f3f4f6',
};

export const type = 'calendar-testimonials';
export const name = 'Testimonials';

type Testimonial = TestimonialsBlockState['cards'][number];

export const initialState: TestimonialsBlockState = {
  headerRichText: '<h2>What Our Clients Say</h2><p>Real results from real people.</p>',
  cards: [
    {
      id: crypto.randomUUID(),
      src: 'https://primer.tailwindui.com/_next/image?url=%2F_next%2Fstatic%2Fmedia%2Favatar-1.fe0baac2.png&w=96&q=75',
      nameRichText: '<strong>Sarah M.</strong>',
      subtitleRichText: '<em>Entrepreneur</em>',
      quoteRichText:
        '<p>"Working with them changed everything. I finally have clarity on my path forward and the confidence to pursue my dreams."</p>',
    },
    {
      id: crypto.randomUUID(),
      src: 'https://images.unsplash.com/photo-1517365830460-955ce3ccd263?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=4&w=320&h=320&q=80',
      nameRichText: '<strong>Michael Chen</strong>',
      subtitleRichText: '<em>Business Owner</em>',
      quoteRichText:
        '<p>"The personalized approach made all the difference. I achieved more in 3 months than I had in the previous year."</p>',
    },
    {
      id: crypto.randomUUID(),
      src: 'https://images.unsplash.com/photo-1502685104226-ee32379fefbe?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=3&w=256&h=256&q=80',
      nameRichText: '<strong>Jessica Taylor</strong>',
      subtitleRichText: '<em>Marketing Director</em>',
      quoteRichText:
        '<p>"Professional, insightful, and truly transformative. I recommend this to anyone looking to level up."</p>',
    },
  ],
};

export const generateState = createGenerateState(initialState);

export const initialBlockState = {
  type,
  paddingt: 'md' as PaddingSize,
  paddingb: 'md' as PaddingSize,
  cardStyles: DEFAULT_TESTIMONIAL_CARD_STYLES,
  iconStyles: DEFAULT_ICON_STYLES,
};

export function MiniView(): JSX.Element {
  return (
    <span class="block py-4">
      <span class="w-4/6 mx-auto block">
        <span class="block bg-gray-500 opacity-75 rounded-sm h-1 mb-3 mx-auto w-1/2"></span>
        <span class="grid grid-cols-2 gap-2">
          <span class="bg-gray-400 opacity-50 rounded p-1 flex flex-col items-center">
            <span class="bg-gray-600 rounded-full h-2 w-2 mb-1"></span>
            <span class="bg-gray-500 opacity-75 rounded-sm h-1 mb-1 w-full"></span>
            <span class="bg-gray-500 opacity-50 rounded-sm h-1 mb-1 w-3/4"></span>
            <span class="bg-gray-600 opacity-75 rounded-sm h-1 w-1/2"></span>
          </span>
          <span class="bg-gray-400 opacity-50 rounded p-1 flex flex-col items-center">
            <span class="bg-gray-600 rounded-full h-2 w-2 mb-1"></span>
            <span class="bg-gray-500 opacity-75 rounded-sm h-1 mb-1 w-full"></span>
            <span class="bg-gray-500 opacity-50 rounded-sm h-1 mb-1 w-3/4"></span>
            <span class="bg-gray-600 opacity-75 rounded-sm h-1 w-1/2"></span>
          </span>
          <span class="bg-gray-400 opacity-50 rounded p-1 flex flex-col items-center">
            <span class="bg-gray-600 rounded-full h-2 w-2 mb-1"></span>
            <span class="bg-gray-500 opacity-75 rounded-sm h-1 mb-1 w-full"></span>
            <span class="bg-gray-500 opacity-50 rounded-sm h-1 mb-1 w-3/4"></span>
            <span class="bg-gray-600 opacity-75 rounded-sm h-1 w-1/2"></span>
          </span>
        </span>
      </span>
    </span>
  );
}

function View(
  props: EditorProps<TestimonialsBlockState> | ViewerProps<TestimonialsBlockState>,
): JSX.Element {
  const { editProps, readonly } = extractReadonlyProps(props);
  const blockState = extractBlockState(props);
  const cardStyles = blockState?.cardStyles || DEFAULT_TESTIMONIAL_CARD_STYLES;
  const iconStyles = blockState?.iconStyles || DEFAULT_ICON_STYLES;

  return (
    <ContainerWrapper>
      <SectionHeader state={props.state} setState={editProps?.setState} />

      <CardLayoutBlock
        blockType={type}
        itemType="Testimonial"
        state={props.state}
        setState={editProps?.setState || (() => {})}
        cardStyles={cardStyles}
        emptyCard={{
          nameRichText: '<strong>Name</strong>',
          subtitleRichText: '<em>Title</em>',
          quoteRichText: '<p>"Add your testimonial here..."</p>',
          src: '',
        }}
        readonly={readonly}
        isBgLight={props.isBgLight}
        isSelected={editProps?.isSelected}
        renderCard={({ item: testimonial, state, setState, readonly, isBgLight }) => {
          const testimonialsWithIndex = (state as any).cards.map((t: Testimonial, i: number) => ({
            ...t,
            i,
          }));
          const t = testimonialsWithIndex.find((item: any) => item.id === testimonial.id)!;

          return (
            <div class="@lg:p-8 @sm:p-6 p-4">
              <div class="@lg:mb-6 @sm:mb-4 mb-3">
                <RichProp
                  state={state}
                  setState={readonly ? undefined : setState}
                  path={['cards', t.i, 'quoteRichText']}
                  class="@lg:text-lg @sm:text-base text-sm leading-relaxed"
                />
              </div>
              <div class="flex items-center">
                <div
                  class={`${getIconClasses(iconStyles)} @lg:mr-4 @sm:mr-3 mr-2 flex-shrink-0`}
                  style={getIconStyle(iconStyles, isBgLight)}
                >
                  <ImgProp
                    state={state}
                    setState={readonly ? undefined : setState}
                    path={['cards', t.i, 'src']}
                    width="60"
                    wrapperClass="w-full h-full"
                    class="w-full h-full object-cover inline-flex items-center justify-center text-gray-400"
                    overlayClass={
                      iconStyles?.shape === 'circle'
                        ? 'rounded-full'
                        : iconStyles?.shape === 'rounded'
                          ? 'rounded-lg'
                          : ''
                    }
                  />
                </div>
                <div class="flex flex-col grow gap-1">
                  <div class="font-medium">
                    <RichProp
                      state={state}
                      setState={readonly ? undefined : setState}
                      path={['cards', t.i, 'nameRichText']}
                      class="testimonial-name-editor"
                    />
                  </div>
                  <div class="opacity-75 @sm:text-sm text-xs">
                    <RichProp
                      state={state}
                      setState={readonly ? undefined : setState}
                      path={['cards', t.i, 'subtitleRichText']}
                      class="testimonial-title-editor"
                    />
                  </div>
                </div>
              </div>
            </div>
          );
        }}
      />
    </ContainerWrapper>
  );
}

export const Editor = View;
export const Viewer = View;
